import R from 'ramda';
import {PAYMENT_DATA, PAYMENT_POLICY_AGREED, PAYMENT_SUBMIT, PAYMENT_VALUE_UPDATED} from '../actions/index';
import {combineReducers} from 'redux';
import {fulfilled, pending, rejected} from '../../../utils/actions';
import {AUTH_PASSWORD_RESET} from '../../auth/actions';

function paymentMethods(state = [], action) {
    switch (action.type) {
        case fulfilled(PAYMENT_DATA):
            return R.map(R.zipObj(['key', 'label']), action.payload.payment_methods || []);
        default:
            return state;
    }
}

function paymentOptions(state = [], action) {
    switch (action.type) {
        case fulfilled(PAYMENT_DATA):
            return [...(action.payload.payment_data || [])];
        default:
            return state;
    }
}

function isRegistered(state = false, action) {
    switch (action.type) {
        case fulfilled(PAYMENT_DATA):
            const user = action.payload.user;
            return !!(user && user.email && user.email.trim());
        default:
            return state;
    }
}

function values(state = {paymentMethod: 'card', paymentOption: null, password: ''}, action) {
    switch (action.type) {
        case PAYMENT_VALUE_UPDATED:
            return {
                ...state,
                [action.payload.key]: action.payload.value,
            };
        case fulfilled(PAYMENT_DATA):
            const userData = action.payload.user || {};
            const isRegistered = !!(userData.email && userData.email.trim());
            
            // For registered users, update with server data
            // For unregistered users, preserve user input and only fill empty fields
            const finalistDiscount = action.payload.finalist_discount || {};
            const discountValues = finalistDiscount.code ? {
                code: state.code || finalistDiscount.code,
                discountPercent: finalistDiscount.discountPercent,
                discountValidUntil: finalistDiscount.discountValidUntil,
            } : {};

            if (isRegistered) {
                return {
                    ...state,
                    ...userData,
                    ...discountValues,
                    paymentOption: state.paymentOption === null
                        ? action.payload.payment_data[0].item.pk
                        : state.paymentOption,
                };
            } else {
                // Preserve user input: only use server data if field is empty in state
                return {
                    ...state,
                    name: state.name || userData.name || '',
                    email: state.email || userData.email || '',
                    phone: state.phone || userData.phone || '',
                    ...discountValues,
                    paymentOption: state.paymentOption === null
                        ? action.payload.payment_data[0].item.pk
                        : state.paymentOption,
                };
            }
        default:
            return state;
    }
}

function termsUrl(state = null, action) {
    switch (action.type) {
        case fulfilled(PAYMENT_DATA):
            return action.payload.terms_url || null;
        default:
            return state;
    }
}

function refundUrl(state = null, action) {
    switch (action.type) {
        case fulfilled(PAYMENT_DATA):
            return action.payload.refund_url || null;
        default:
            return state;
    }
}

function policyAgreed(state = true, action) {
    switch (action.type) {
        case PAYMENT_POLICY_AGREED:
            return action.payload;
        default:
            return state;
    }
}

function isFormVisible(state = true, action) {
    switch (action.type) {
        case fulfilled(PAYMENT_DATA):
            // Форма оплаты видна по умолчанию, скрыта только если API явно указал is_form_visible: false
            // Для демо-уроков is_form_visible всегда false, для остальных - true
            return action.payload.is_form_visible !== false;
        default:
            return state;
    }
}

function fields(state = {}, action) {
    switch (action.type) {
        case fulfilled(PAYMENT_DATA):
            return {...action.payload.fields};
        default:
            return state;
    }
}

function isDemo(state = false, action) {
    switch (action.type) {
        case fulfilled(PAYMENT_DATA):
            return action.payload.is_demo || false;
        default:
            return state;
    }
}

function productUrl(state = null, action) {
    switch (action.type) {
        case fulfilled(PAYMENT_DATA):
            return action.payload.product_url || null;
        default:
            return state;
    }
}

// Field name mapping from backend to frontend
const FIELD_NAME_MAP = {
    'freeTime': 'freeTime', // Backend uses camelCase, frontend also uses camelCase
    'free_time': 'freeTime', // Handle snake_case from backend
    'startComment': 'startComment',
    'start_comment': 'startComment',
};

function errors(state = {}, action) {
    switch (action.type) {
        case rejected(PAYMENT_DATA):
        case rejected(PAYMENT_SUBMIT):
            // Extract errors from BadRequest object or directly from payload
            let errorData = {};
            if (action.payload) {
                // Check for BadRequest object with data property (has status 400 and data)
                if (action.payload.data && typeof action.payload.data === 'object') {
                    // BadRequest object with data property
                    errorData = action.payload.data;
                } else if (action.payload.status === 400 && action.payload.data) {
                    // Alternative BadRequest structure
                    errorData = action.payload.data;
                } else if (typeof action.payload === 'object' && !action.payload.status) {
                    // Direct error object (not BadRequest, no status property)
                    errorData = action.payload;
                } else if (action.payload.status === 400) {
                    // BadRequest but data might be at root level
                    errorData = action.payload;
                }
            }
            // Map field names from backend to frontend
            const mappedErrors = {};
            Object.keys(errorData).forEach(key => {
                if (key === '__onClick' || key === 'status' || key === 'message' || key === 'stack') return;
                // Map field name if mapping exists, otherwise use original
                const mappedKey = FIELD_NAME_MAP[key] || key;
                mappedErrors[mappedKey] = errorData[key];
            });
            // Log errors for debugging
            if (Object.keys(mappedErrors).length > 0) {
                console.log('[PaymentForm] Validation errors:', mappedErrors);
            } else if (action.payload) {
                console.log('[PaymentForm] No field errors found in payload:', action.payload);
            }
            return {...mappedErrors};
        case pending(PAYMENT_DATA):
        case pending(PAYMENT_SUBMIT):
            // Clear errors when starting new request
            return {};
        default:
            return state;
    }
}

function isFetching(state = false, action) {
    switch (action.type) {
        case fulfilled(PAYMENT_DATA):
        case fulfilled(PAYMENT_SUBMIT):
        case rejected(PAYMENT_DATA):
        case rejected(PAYMENT_SUBMIT):
            return false;
        case pending(PAYMENT_DATA):
        case pending(PAYMENT_SUBMIT):
            return true;
        default:
            return state;
    }
}

function isPasswordResetting(state = false, action) {
    switch (action.type) {
        case fulfilled(AUTH_PASSWORD_RESET):
        case rejected(AUTH_PASSWORD_RESET):
            return false;
        case pending(AUTH_PASSWORD_RESET):
            return true;
        default:
            return state;
    }
}

function offerProductsList(state = false, action) {
    switch (action.type) {
        case fulfilled(PAYMENT_DATA):
            return action.payload.products || null;
        default:
            return state;
    }
}

function finalistDiscount(state = null, action) {
    switch (action.type) {
        case fulfilled(PAYMENT_DATA):
            return action.payload.finalist_discount || null;
        default:
            return state;
    }
}

export default combineReducers({
    paymentMethods,
    isRegistered,
    values,
    termsUrl,
    refundUrl,
    policyAgreed,
    paymentOptions,
    isFormVisible,
    fields,
    isDemo,
    productUrl,
    errors,
    isFetching,
    isPasswordResetting,
    offerProductsList,
    finalistDiscount,
});



// WEBPACK FOOTER //
// ./src/portal/payment/reducers/index.js